import { useMemo, useState } from "react";
import { Label } from "./ui-bits";
import { MEDICATIONS, SYRINGES, DEFAULT_DEAD_SPACE_ML, calcSyringeFill, round } from "@/data/tools";

function SyringeVisual({ syringe, fillPct, totalMl, exceeds }) {
  const ticks = [];
  for (let v = 0; v <= syringe.capacity + 1e-9; v += syringe.majorStep) ticks.push(round(v, 2));
  return (
    <div className="relative" data-testid="syringe-visual">
      <svg viewBox="0 0 640 140" className="w-full h-auto" role="img" aria-label={`Syringe showing fill at ${totalMl ?? 0} mL`}>
        {/* barrel */}
        <rect x="40" y="40" width="520" height="56" rx="10" fill="#0E131F" stroke="rgba(255,255,255,0.14)" />
        {/* fill */}
        <rect
          x="40"
          y="40"
          width={(520 * Math.min(fillPct || 0, 100)) / 100}
          height="56"
          rx="10"
          fill={exceeds ? "rgba(255,99,132,0.35)" : "rgba(0,229,192,0.28)"}
          style={{ transition: "width .5s cubic-bezier(.16,1,.3,1)" }}
        />
        {/* plunger + tip */}
        <rect x="560" y="52" width="60" height="32" rx="4" fill="#1b2233" stroke="rgba(255,255,255,0.14)" />
        <rect x="8" y="60" width="32" height="16" rx="3" fill="#1b2233" stroke="rgba(255,255,255,0.14)" />
        {/* ticks */}
        {ticks.map((t) => {
          const x = 40 + (520 * t) / syringe.capacity;
          return (
            <g key={t}>
              <line x1={x} y1="40" x2={x} y2="56" stroke="rgba(244,246,248,0.6)" strokeWidth="1.2" />
              <text x={x} y="118" textAnchor="middle" fontSize="12" fill="#8a94a6" fontFamily="JetBrains Mono, monospace">
                {t}
              </text>
            </g>
          );
        })}
        {/* fill marker */}
        {totalMl !== null && totalMl !== undefined && !exceeds && (
          <g>
            <line x1={40 + (520 * fillPct) / 100} y1="24" x2={40 + (520 * fillPct) / 100} y2="104" stroke="#00E5C0" strokeWidth="2" strokeDasharray="4 3" />
            <text x={40 + (520 * fillPct) / 100} y="16" textAnchor="middle" fontSize="12" fill="#00E5C0" fontFamily="JetBrains Mono, monospace">
              fill here · {totalMl} mL
            </text>
          </g>
        )}
      </svg>
    </div>
  );
}

export default function SyringeGuide() {
  const [medId, setMedId] = useState(MEDICATIONS[0].id);
  const [customMgPerMl, setCustomMgPerMl] = useState("");
  const [mode, setMode] = useState("mg"); // mg | ml
  const [dose, setDose] = useState("");
  const [syringeId, setSyringeId] = useState("1");
  const [includeDeadSpace, setIncludeDeadSpace] = useState(true);

  const med = MEDICATIONS.find((m) => m.id === medId);
  const mgPerMl = med.mgPerMl ?? Number(customMgPerMl);
  const syringe = SYRINGES.find((s) => s.id === syringeId);
  const deadSpace = includeDeadSpace ? DEFAULT_DEAD_SPACE_ML : 0;

  const mg = useMemo(() => {
    const d = Number(dose);
    if (!d || d <= 0) return 0;
    return mode === "mg" ? d : d * mgPerMl;
  }, [dose, mode, mgPerMl]);

  const result = calcSyringeFill({ mg, mgPerMl, deadSpaceMl: deadSpace, capacity: syringe.capacity });

  return (
    <div className="grid lg:grid-cols-12 gap-6" data-testid="syringe-guide">
      <div className="lg:col-span-5 card-line rounded-2xl p-6 sm:p-8 space-y-5">
        <Label>Inputs</Label>
        <div>
          <label htmlFor="sg-med" className="mono-label text-dusk block mb-2">Medication</label>
          <select id="sg-med" className="field" value={medId} onChange={(e) => setMedId(e.target.value)} data-testid="syringe-medication">
            {MEDICATIONS.map((m) => (
              <option key={m.id} value={m.id} className="bg-surface">{m.label}</option>
            ))}
          </select>
          {med.mgPerMl === null ? (
            <input type="number" min="1" step="1" className="field mt-3" placeholder="Concentration in mg/mL" value={customMgPerMl} onChange={(e) => setCustomMgPerMl(e.target.value)} data-testid="syringe-custom-concentration" />
          ) : (
            <p className="text-xs text-dusk mt-2">Concentration: <span className="text-bone/80 font-mono">{med.mgPerMl} mg/mL</span></p>
          )}
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <label htmlFor="sg-dose" className="mono-label text-dusk">Your prescribed dose</label>
            <div className="inline-flex rounded-full border border-line p-0.5">
              {["mg", "ml"].map((m) => (
                <button key={m} type="button" onClick={() => setMode(m)} className={`px-3 py-1 rounded-full text-xs font-mono transition-colors ${mode === m ? "bg-teal text-ink" : "text-mute"}`} data-testid={`syringe-mode-${m}`}>
                  {m === "mg" ? "mg" : "mL"}
                </button>
              ))}
            </div>
          </div>
          <input id="sg-dose" type="number" min="0" step={mode === "mg" ? "5" : "0.01"} className="field" placeholder={mode === "mg" ? "e.g. 100" : "e.g. 0.5"} value={dose} onChange={(e) => setDose(e.target.value)} data-testid="syringe-dose" />
          {mg > 0 && mgPerMl > 0 && (
            <p className="text-xs text-dusk mt-2">
              = <span className="text-bone/80 font-mono">{round(mg, 1)} mg</span> testosterone · <span className="text-bone/80 font-mono">{round(mg / mgPerMl, 3)} mL</span>
            </p>
          )}
        </div>

        <div>
          <label htmlFor="sg-syringe" className="mono-label text-dusk block mb-2">Syringe</label>
          <select id="sg-syringe" className="field" value={syringeId} onChange={(e) => setSyringeId(e.target.value)} data-testid="syringe-size">
            {SYRINGES.map((s) => (
              <option key={s.id} value={s.id} className="bg-surface">{s.label}</option>
            ))}
          </select>
        </div>

        <label className="flex items-center gap-3 text-sm text-bone/80 cursor-pointer">
          <input type="checkbox" checked={includeDeadSpace} onChange={(e) => setIncludeDeadSpace(e.target.checked)} className="accent-[#00E5C0] w-4 h-4" data-testid="syringe-deadspace" />
          Include {DEFAULT_DEAD_SPACE_ML} mL dead space (needle hub / tip)
        </label>
      </div>

      <div className="lg:col-span-7 card-line rounded-2xl p-6 sm:p-8">
        <Label>Fill target</Label>
        <SyringeVisual syringe={syringe} fillPct={result?.fillPct ?? 0} totalMl={result?.totalMl ?? null} exceeds={result?.exceeds} />
        <dl className="grid grid-cols-3 gap-4 mt-6" data-testid="syringe-results">
          {[
            ["Dose volume", result ? `${result.doseMl} mL` : "—"],
            ["Dead space", `${deadSpace} mL`],
            ["Total fill", result ? `${result.totalMl} mL` : "—"],
          ].map(([k, v]) => (
            <div key={k} className="rounded-xl border border-line p-4">
              <dt className="mono-label text-dusk">{k}</dt>
              <dd className="font-display text-2xl sm:text-3xl text-teal mt-1" data-testid={`syringe-result-${k.toLowerCase().replace(/\s/g, "-")}`}>{v}</dd>
            </div>
          ))}
        </dl>
        {result?.exceeds && (
          <p className="text-rose text-sm mt-4" data-testid="syringe-warning-exceeds">This volume exceeds the selected syringe. Choose a larger syringe or check your dose with your clinician.</p>
        )}
        {result?.tooSmall && (
          <p className="text-amber-300 text-sm mt-4" data-testid="syringe-warning-small">A small volume like this is easier to measure accurately on a 0.5 or 1 mL syringe.</p>
        )}
        <p className="text-xs text-dusk leading-relaxed mt-6">
          Fill = dose volume + dead space. This visual helps you measure a volume you have already
          been prescribed. It does not teach injection technique and never replaces your clinician's
          or pharmacist's instructions. If unsure, ask before you inject.
        </p>
      </div>
    </div>
  );
}
